import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { supabase } from '../../../lib/supabaseClient';

/**
 * SkillSelector - Multi-select component for skills
 *
 * Props:
 * - selectedSkillIds: Array of selected skill UUIDs
 * - onChange: Callback(skillIds) when selection changes
 */
const SkillSelector = ({ selectedSkillIds = [], onChange }) => {
  const [allSkills, setAllSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchAllSkills();
  }, []);

  const fetchAllSkills = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('skills_master')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setAllSkills(data || []);
    } catch (err) {
      console.error('Error fetching skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = (skillId) => {
    if (!selectedSkillIds.includes(skillId)) {
      onChange([...selectedSkillIds, skillId]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleRemoveSkill = (skillId) => {
    onChange(selectedSkillIds.filter(id => id !== skillId));
  };

  const selectedSkills = allSkills.filter(skill => selectedSkillIds.includes(skill.id));
  const filteredSkills = allSkills.filter(skill =>
    !selectedSkillIds.includes(skill.id) &&
    skill.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-2">
      {/* Selected Skills */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedSkills.map(skill => (
            <Badge key={skill.id} className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
              {skill.name}
              <button
                onClick={() => handleRemoveSkill(skill.id)}
                className="ml-2 hover:text-indigo-600"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search skills..."
          className="w-full border rounded-md px-3 py-2 bg-background"
        />

        {/* Dropdown */}
        {showDropdown && searchTerm && (
          <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading skills...</div>
            ) : filteredSkills.length > 0 ? (
              filteredSkills.slice(0, 50).map(skill => (
                <button
                  key={skill.id}
                  onClick={() => handleAddSkill(skill.id)}
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                >
                  {skill.name}
                </button>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No matching skills found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Close dropdown when clicking outside */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default SkillSelector;
